import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Search, Building2, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import { OrgForm } from "../components/forms";
import PrintButton from "../components/PrintButton";
import PrintHeader from "../components/PrintHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ORG_TYPES, ORG_TYPE_LABELS, PROVINCES } from "../lib/constants";

export default function Structures() {
  const { hasPerm } = useAuth();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [province, setProvince] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback((p = page, ps = pageSize, query = q, t = type, prov = province) => {
    setLoading(true);
    API.get("/organizations", { params: { q: query, type: t, province: prov, page: p, page_size: ps } })
      .then((r) => { setResult(r.data); setPage(p); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, type, province]);

  useEffect(() => { load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openCreate = () => { setEditing(null); setForm({ type: "ENTREPRISE_TRANSPORT", statut: "ACTIF" }); setDialogOpen(true); };
  const openEdit = (org) => { setEditing(org.id); setForm(org); setDialogOpen(true); };
  const openDetail = (org) => {
    API.get(`/organizations/${org.id}`).then((r) => { setDetail(r.data); setDetailOpen(true); });
  };

  const save = async () => {
    if (!form.nom) { toast.error("Le nom est obligatoire."); return; }
    setSaving(true);
    try {
      if (editing) {
        await API.put(`/organizations/${editing}`, form);
        toast.success("Structure mise a jour.");
      } else {
        await API.post("/organizations", form);
        toast.success("Structure creee.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="structures-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Structures</h1>
          <p className="text-sm text-muted-foreground">Ministeres, administrations, entreprises et operateurs de transport.</p>
        </div>
        {hasPerm("EXPORT_DATA") && <PrintButton testid="print-structures" />}
        {hasPerm("CREATE_ORGANIZATION") && (
          <Button onClick={openCreate} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="org-create">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle structure
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Nom ou sigle..." className="pl-9" data-testid="orgs-search" />
        </div>
        <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); load(1, pageSize, q, v === "all" ? "" : v, province); }}>
          <SelectTrigger className="w-56" data-testid="orgs-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {ORG_TYPES.map((t) => <SelectItem key={t} value={t}>{ORG_TYPE_LABELS[t]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={province || "all"} onValueChange={(v) => { setProvince(v === "all" ? "" : v); load(1, pageSize, q, type, v === "all" ? "" : v); }}>
          <SelectTrigger className="w-48" data-testid="orgs-province"><SelectValue placeholder="Province" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes provinces</SelectItem>
            {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => load(1)} variant="secondary" data-testid="orgs-apply">Filtrer</Button>
      </div>

      {loading && !result ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <>
          {result && <PrintHeader title="Liste des structures" subtitle={`${result.total.toLocaleString("fr-FR")} enregistrement(s) — page ${page}`} />}
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-accent/60 text-left">
                <tr>{["Nom", "Sigle", "Type", "Ville", "Province", "Conducteurs", "Vehicules", "Statut", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(result?.items || []).map((o, i) => (
                  <tr key={o.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`org-row-${i}`}>
                    <td className="px-3 py-2.5 font-medium max-w-56 truncate">{o.nom}</td>
                    <td className="px-3 py-2.5 mono text-xs">{o.sigle || "-"}</td>
                    <td className="px-3 py-2.5"><Badge variant="secondary" className="text-[10px]">{ORG_TYPE_LABELS[o.type] || o.type}</Badge></td>
                    <td className="px-3 py-2.5 text-xs">{o.ville || "-"}</td>
                    <td className="px-3 py-2.5 text-xs">{o.province || "-"}</td>
                    <td className="px-3 py-2.5 text-center">{o.nb_conducteurs}</td>
                    <td className="px-3 py-2.5 text-center">{o.nb_vehicules}</td>
                    <td className="px-3 py-2.5"><StatusBadge value={o.statut} /></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(o)} data-testid={`org-view-${i}`}><Eye className="h-4 w-4" /></Button>
                      {hasPerm("UPDATE_ORGANIZATION") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid={`org-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
                {result && result.items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">Aucune structure trouvee.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result && (
            <Pagination total={result.total} page={page} pageSize={pageSize}
              onPage={(p) => load(p)} onPageSize={(s) => { setPageSize(s); load(1, s); }} testid="orgs-pagination" />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="org-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier la structure" : "Nouvelle structure"}</DialogTitle>
          </DialogHeader>
          <OrgForm initial={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="org-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="org-save">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="org-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-[#3A75C4]" /> {detail.organization.nom}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex gap-2">
                  <Badge variant="secondary">{ORG_TYPE_LABELS[detail.organization.type]}</Badge>
                  <StatusBadge value={detail.organization.statut} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-muted-foreground">Sigle</p><p className="mono">{detail.organization.sigle || "-"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Responsable</p><p>{detail.organization.responsable || "-"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Telephone</p><p className="mono">{detail.organization.telephone || "-"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Email</p><p>{detail.organization.email || "-"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Ville</p><p>{detail.organization.ville || "-"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Province</p><p>{detail.organization.province || "-"}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-3 text-center">
                    <p className="text-2xl font-heading font-bold text-[#009E60]">{detail.organization.nb_conducteurs}</p>
                    <p className="text-xs text-muted-foreground">Conducteurs</p>
                  </div>
                  <div className="rounded-lg border p-3 text-center">
                    <p className="text-2xl font-heading font-bold text-[#3A75C4]">{detail.organization.nb_vehicules}</p>
                    <p className="text-xs text-muted-foreground">Vehicules</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Conducteurs (aperçu)</p>
                  <div className="rounded-lg border divide-y">
                    {detail.drivers.length === 0 && <p className="p-3 text-sm text-muted-foreground">Aucun conducteur rattache.</p>}
                    {detail.drivers.map((d) => (
                      <Link key={d.id} to={`/conducteurs/${d.id}`} onClick={() => setDetailOpen(false)}
                        className="flex items-center justify-between px-3 py-2 text-sm hover:bg-accent" data-testid={`org-driver-${d.id}`}>
                        <span><b>{d.nom}</b> {d.prenom}</span>
                        <span className="mono text-xs text-muted-foreground">{d.telephone}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
